<script lang="ts">
  // The mockup's consolidated per-row "⋯" menu — one trigger + popover list,
  // replacing a row of individual icon buttons. Closes on Escape, outside
  // click, or focus leaving the menu. The menu uses fixed viewport positioning
  // so it is not clipped by table overflow containers.
  import { tick } from 'svelte';
  import { MoreHorizontal } from 'lucide-svelte';
  import { t } from '$lib/i18n';

  const {
    items
  }: { items: { label: string; onClick: () => void; danger?: boolean }[] } = $props();

  let open = $state(false);
  let trigger = $state<HTMLButtonElement | undefined>();
  let menu = $state<HTMLDivElement | undefined>();
  let menuStyle = $state('');

  const VIEWPORT_PAD = 8;
  const GAP = 4;

  function close() {
    open = false;
  }

  function positionMenu() {
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuWidth = menu?.offsetWidth ?? 150;
    const menuHeight = menu?.offsetHeight ?? items.length * 32 + 10;

    let top = rect.bottom + GAP;
    let left = rect.right - menuWidth;

    if (top + menuHeight > window.innerHeight - VIEWPORT_PAD) {
      const above = rect.top - menuHeight - GAP;
      if (above >= VIEWPORT_PAD) {
        top = above;
      }
    }

    left = Math.max(VIEWPORT_PAD, Math.min(left, window.innerWidth - menuWidth - VIEWPORT_PAD));
    top = Math.max(VIEWPORT_PAD, Math.min(top, window.innerHeight - menuHeight - VIEWPORT_PAD));

    menuStyle = `top:${top}px;left:${left}px`;
  }

  async function toggle() {
    if (open) {
      close();
      return;
    }
    open = true;
    await tick();
    positionMenu();
  }

  function onWindowClick(e: MouseEvent) {
    if (!open) return;
    const target = e.target as Node;
    if (trigger?.contains(target) || menu?.contains(target)) return;
    close();
  }

  function onWindowKeydown(e: KeyboardEvent) {
    if (open && e.key === 'Escape') close();
  }

  function onFocusOut(e: FocusEvent) {
    const next = e.relatedTarget as Node | null;
    if (!next) return;
    if (trigger?.contains(next) || menu?.contains(next)) return;
    close();
  }

  function run(action: () => void) {
    close();
    action();
  }

  $effect(() => {
    if (!open) return;
    const reposition = () => positionMenu();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  });
</script>

<svelte:window onclick={onWindowClick} onkeydown={onWindowKeydown} />

<div class="relative inline-block" onfocusout={onFocusOut}>
  <button
    type="button"
    bind:this={trigger}
    aria-label={t('a11y.rowActions')}
    aria-haspopup="menu"
    aria-expanded={open}
    class="flex h-7 w-7 items-center justify-center rounded-md text-dark-gray hover:bg-surface-2 hover:text-navy"
    onclick={toggle}
  >
    <MoreHorizontal class="h-4 w-4" />
  </button>
</div>
{#if open}
  <div
    bind:this={menu}
    role="menu"
    class="fixed z-50 flex min-w-[150px] flex-col gap-px rounded-[10px] border border-toyama-cloudy-gray bg-surface p-[5px]"
    style="box-shadow:var(--tym-shadow-3);{menuStyle}"
    onfocusout={onFocusOut}
  >
    {#each items as item (item.label)}
      <button
        type="button"
        role="menuitem"
        class="block w-full rounded-md px-2.5 py-1.5 text-left text-[12px] hover:bg-surface-2 {item.danger
          ? 'text-danger'
          : 'text-toyama-heavy-carbon'}"
        onclick={() => run(item.onClick)}
      >
        {item.label}
      </button>
    {/each}
  </div>
{/if}
